/**
 * Quick Read - AI-powered Product Description Summary
 * Features:
 * - Caches summaries per product and language to avoid repeated API calls
 * - Uses admin-configured settings for API, model, and fallback
 * - Renders instantly if summary is already cached
 * - Shows fallback message on API failure
 * - Automatically invalidates cache when product description changes
 */

/**
 * Generate a simple hash of a string for cache invalidation
 * Uses djb2 algorithm - fast and produces consistent hashes
 * @param {string} str - The string to hash
 * @returns {string} - Hash as a hex string
 */
function quickReadHash(str) {
    if (!str) return '0';
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) + str.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
    }
    // Convert to unsigned and then to hex for consistent positive hash
    return (hash >>> 0).toString(16);
}

class QuickRead {
    constructor(container) {
        this.container = container;
        this.toggle = container.querySelector('.quick-read-toggle');
        this.content = container.querySelector('.quick-read-content');
        this.bulletsContainer = container.querySelector('.quick-read-bullets');
        this.loadingEl = container.querySelector('.quick-read-loading');
        this.errorEl = container.querySelector('.quick-read-error');
        this.fallbackEl = container.querySelector('.quick-read-fallback');
        this.retryBtn = container.querySelector('.quick-read-retry-btn');
        this.feedbackBtns = container.querySelectorAll('.quick-read-feedback-btn');
        this.bulletsData = [];
        this.activeIntervals = [];

        // Get product data and settings from script tag
        const dataScript = container.querySelector('script[type="application/json"]');
        if (!dataScript) {
            console.error('Quick Read: No data script found');
            return;
        }

        try {
            const data = JSON.parse(dataScript.textContent);
            this.productId = data.productId;
            this.productTitle = data.productTitle;
            this.productDescription = data.productDescription;
            this.sectionId = data.sectionId;
            this.settings = data.settings || {};
        } catch (e) {
            console.error('Quick Read: Failed to parse data', e);
            return;
        }

        this.isOpen = false;
        this.isLoaded = false;
        this.isLoading = false;

        // Cache key uses product ID
        this.cacheKey = `quick_read_${this.productId}`;
        this.cacheDays = this.settings.cacheDays || 7;

        // Generate hash of current description for cache invalidation
        // When description changes, hash changes, causing cache invalidation
        this.descriptionHash = quickReadHash(this.productDescription);

        this.init();
    }
updateTitle() {
    const titleElement = this.container.querySelector('.quick-read-title');
    if (!titleElement) return;

    titleElement.textContent = this.isOpen
        ? 'Quick Read'
        : 'Show Quick Read';
}

    init() {
        // Check if we have a valid cached summary
        const cachedData = this.getCachedSummary();
        if (cachedData) {
            this.displayBullets(cachedData.bullets);
            this.isLoaded = true;
        }
            this.updateTitle();


        // Toggle event
        this.toggle.addEventListener('click', () => this.handleToggle());

        // Retry button event
        if (this.retryBtn) {
            this.retryBtn.addEventListener('click', () => {
                this.hideError();
                this.hideFallback();
                this.generateSummary();
            });
        }

        // Feedback buttons event
        this.feedbackBtns.forEach(btn => {
            btn.addEventListener('click', (e) => this.handleFeedback(e));
        });
    }

handleToggle() {
    this.isOpen = !this.isOpen;
    this.toggle.setAttribute('aria-expanded', this.isOpen);
    this.content.setAttribute('aria-hidden', !this.isOpen);

        const titleElement = this.container.querySelector('.quick-read-title');
    this.updateTitle();


    if (this.isOpen) {
        this.content.classList.add('is-open');

        if (this.bulletsData.length) {
            this.stopTypingAnimation();
            this.resetBulletsForTyping();
            this.startTypingAnimation();
        } else if (!this.isLoaded && !this.isLoading) {
            this.generateSummary();
        }

    } else {
        this.content.classList.remove('is-open');
        this.stopTypingAnimation(); // ❗ do NOT reset here
    }
}

    startTypingAnimation() {
    const items = Array.from(this.bulletsContainer.querySelectorAll('li'));
    let itemIndex = 0;

    const typeNext = () => {
        if (itemIndex >= items.length) return;

        const li = items[itemIndex];
        const text = li.dataset.text;
        let charIndex = 0;

        li.style.opacity = '1';

        const interval = setInterval(() => {
            li.textContent += text.charAt(charIndex++);
            if (charIndex >= text.length) {
                clearInterval(interval);
                itemIndex++;
                setTimeout(typeNext, 300);
            }
        }, 12);

        this.activeIntervals.push(interval);
    };

    typeNext();
}

stopTypingAnimation() {
    this.activeIntervals.forEach(i => clearInterval(i));
    this.activeIntervals = [];
}

resetBulletsForTyping() {
    const items = this.bulletsContainer.querySelectorAll('li');
    items.forEach(li => {
        li.textContent = '';
        li.style.opacity = '0';
    });
}

    /**
     * Get cached summary from localStorage
     * Returns null if cache is expired OR if the product description has changed
     */
    getCachedSummary() {
        try {
            const cached = localStorage.getItem(this.cacheKey);
            if (!cached) return null;

            const data = JSON.parse(cached);
            const now = Date.now();
            const expiryMs = this.cacheDays * 24 * 60 * 60 * 1000;

            // Check if cache is still valid (not expired)
            if (!data.timestamp || (now - data.timestamp) >= expiryMs) {
                localStorage.removeItem(this.cacheKey);
                console.log('Quick Read: Cache expired, will regenerate summary');
                return null;
            }

            // If cached entry has NO descriptionHash, it's an old entry - invalidate it
            if (!data.descriptionHash) {
                localStorage.removeItem(this.cacheKey);
                console.log('Quick Read: Old cache format detected, regenerating summary');
                return null;
            }

            // Check if product description has changed since cache was created
            if (data.descriptionHash !== this.descriptionHash) {
                localStorage.removeItem(this.cacheKey);
                console.log('Quick Read: Product description changed, regenerating summary');
                return null;
            }

            // Cache is valid and description hasn't changed
            return data;
        } catch (e) {
            console.warn('Quick Read: Error reading cache', e);
            return null;
        }
    }

    /**
     * Save summary to localStorage cache
     * Includes description hash for future cache invalidation
     */
    setCachedSummary(bullets) {
        try {
            const data = {
                bullets: bullets,
                timestamp: Date.now(),
                productId: this.productId,
                descriptionHash: this.descriptionHash // Store hash for change detection
            };
            localStorage.setItem(this.cacheKey, JSON.stringify(data));
        } catch (e) {
            console.warn('Quick Read: Error saving cache', e);
        }
    }

    async generateSummary() {
        if (!this.productDescription) {
            this.showFallback();
            return;
        }

        // Check for proxy URL
        if (!this.settings.proxyUrl) {
            console.warn('Quick Read: No proxy URL configured');
            this.showFallback();
            return;
        }

        this.isLoading = true;
        this.showLoading();
        this.hideError();
        this.hideFallback();

        try {
            const bullets = await this.callProxy(this.productDescription);

            if (bullets && bullets.length > 0) {
                // Cache the result
                this.setCachedSummary(bullets);

                // Display bullets
                this.displayBullets(bullets);
                this.isLoaded = true;
            } else {
                throw new Error('No bullets generated');
            }
        } catch (error) {
            console.error('Quick Read Error:', error);
            this.showFallback();
        } finally {
            this.isLoading = false;
            this.hideLoading();
        }
    }

    /**
     * Call the secure server-side proxy to generate summary
     * The proxy handles OpenAI API calls with the API key stored securely on the server
     */
    async callProxy(description) {
        const prompt = `Analyze the following product description and create exactly 3-4 concise bullet points that highlight the key features and benefits. Each bullet point should be a complete sentence, informative, and help customers quickly understand the product. Keep each bullet under 25 words.

Product Description:
${description}

Format your response as a JSON array of strings, like this:
["First bullet point", "Second bullet point", "Third bullet point"]

Only return the JSON array, no other text.`;

        // Call the secure proxy - API key is stored on the server
        const response = await fetch(this.settings.proxyUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                prompt: prompt,
                max_tokens: 300
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `API request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.error || 'API request failed');
        }

        const content = data.summary?.trim();

        if (!content) {
            throw new Error('Empty response from API');
        }

        // Parse JSON response
        try {
            const bullets = JSON.parse(content);
            if (Array.isArray(bullets) && bullets.length > 0) {
                return bullets;
            }
            throw new Error('Invalid response format');
        } catch (parseError) {
            // Try to extract bullet points if JSON parsing fails
            const lines = content.split('\n').filter(line => line.trim());
            if (lines.length > 0) {
                return lines.map(line => line.replace(/^[-•*"\[\]]\s*/, '').replace(/["\[\],]*$/, '').trim()).filter(l => l);
            }
            throw new Error('Could not parse API response');
        }
    }

displayBullets(bullets) {
    if (!this.bulletsContainer) return;

    // ✅ persist bullets
    this.bulletsData = bullets;

    this.bulletsContainer.innerHTML = '';
    this.stopTypingAnimation();

    bullets.forEach(text => {
        const li = document.createElement('li');
        li.dataset.text = text;
        li.textContent = '';
        li.style.opacity = '0';
        this.bulletsContainer.appendChild(li);
    });

    this.startTypingAnimation();

    const footer = this.container.querySelector('.quick-read-footer');
    if (footer) footer.style.display = 'flex';
}


    showLoading() {
        if (this.loadingEl) {
            this.loadingEl.style.display = 'flex';
        }
        this.bulletsContainer.innerHTML = '';

        // Hide footer during loading
        const footer = this.container.querySelector('.quick-read-footer');
        if (footer) {
            footer.style.display = 'none';
        }
    }

    hideLoading() {
        if (this.loadingEl) {
            this.loadingEl.style.display = 'none';
        }
    }

    showError() {
        if (this.errorEl) {
            this.errorEl.style.display = 'flex';
        }
    }

    hideError() {
        if (this.errorEl) {
            this.errorEl.style.display = 'none';
        }
    }

    showFallback() {
        // Show fallback message instead of error with retry
        if (this.fallbackEl) {
            this.fallbackEl.style.display = 'block';
        }

        // Hide footer when showing fallback
        const footer = this.container.querySelector('.quick-read-footer');
        if (footer) {
            footer.style.display = 'none';
        }

        this.isLoaded = true; // Mark as loaded to prevent further attempts
    }

    hideFallback() {
        if (this.fallbackEl) {
            this.fallbackEl.style.display = 'none';
        }
    }

    handleFeedback(event) {
        const btn = event.currentTarget;
        const feedback = btn.dataset.feedback;

        // Remove active state from all buttons
        this.feedbackBtns.forEach(b => b.classList.remove('is-active'));

        // Add active state to clicked button
        btn.classList.add('is-active');

        // Log feedback (can be sent to analytics)
        console.log('Quick Read feedback:', {
            productId: this.productId,
            feedback: feedback
        });

        // Show thank you message
        const feedbackText = this.container.querySelector('.quick-read-feedback');
        if (feedbackText) {
            feedbackText.textContent = 'Thanks for your feedback!';
        }
    }

    /**
     * Clear cache for this product (useful for admin/testing)
     */
    clearCache() {
        try {
            localStorage.removeItem(this.cacheKey);
            this.isLoaded = false;
            this.bulletsContainer.innerHTML = '';
        } catch (e) {
            console.warn('Quick Read: Error clearing cache', e);
        }
    }
}

// Utility function to clear all Quick Read caches
window.clearAllQuickReadCaches = function () {
    try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('quick_read_')) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log(`Cleared ${keysToRemove.length} Quick Read cache entries`);
    } catch (e) {
        console.error('Error clearing Quick Read caches', e);
    }
};

// Initialize all Quick Read components on the page
document.addEventListener('DOMContentLoaded', () => {
    const quickReadContainers = document.querySelectorAll('.quick-read-container');

    quickReadContainers.forEach(container => {
        new QuickRead(container);
    });
});

// toggleBtn.addEventListener("click", () => {
//   const expanded = toggleBtn.getAttribute("aria-expanded") === "true";
 
//   toggleBtn.setAttribute("aria-expanded", !expanded);
//   content.setAttribute("aria-hidden", expanded);
// });
